import React, { useState, useEffect, useRef } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../services/Interceptor";
import { fetchProfilePhoto } from "../store/AthleteActions";
import AutoLogin from "../pages/public/AutoLogin";
import { broadcastLogout } from "../utils/authChannel";

const Navbar = ({ fixedMobile = false }) => {
  const { user } = useAuthUser() || "";
  const logOut = useSignOut();
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const { profilePhoto } = useSelector((state) => state.athletes);
  const hasSubscription = useSelector((state) => state.user.hasSubscription);

  const [profilePicture, setProfilePicture] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (user) {
      dispatch(fetchProfilePhoto(apiInstance, user?.id));
    }
  }, [dispatch, apiInstance, user?.id]); // eslint-disable-line

  useEffect(() => {
    if (profilePhoto) {
      setProfilePicture(profilePhoto);
    }
  }, [profilePhoto]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const canViewLeaderboard =
    user?.role === "university" ||
    user?.role === "highschool" ||
    hasSubscription;
  const dashboardPath =
    user?.role === "highschool"
      ? "/highschool/dashboard"
      : `/${user?.role}/dashboard`;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const style = {
    active: "text-xl uppercase my-auto text-red-500 hover:underline",
    inactive:
      "text-xl uppercase text-white my-auto hover:text-red-500 hover:underline ",
    isPending: "",
  };

  const signOut = () => {
    logOut();
    broadcastLogout();
    setProfilePicture(null);
    window.location.href = "/sign-in";
  };

  return (
    <>
      <nav
        className={`transition-all duration-300 py-3 px-4 md:px-4 xl:px-16 
           ${
             isScrolled
               ? "bg-[#363636] shadow-md"
               : fixedMobile
                 ? "bg-[#363636] shadow-md lg:bg-transparent lg:shadow-none"
                 : "bg-transparent"
           } 
           ${fixedMobile ? "fixed lg:relative" : "relative"} top-0 left-0 w-full z-20
           `}
      >
        <div className="mx-auto flex justify-between items-center px-0 md:px-4 md:justify-between">
          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-gray-900"
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? (
              <FontAwesomeIcon
                icon={faTimes}
                size="lg"
                className="text-white"
              />
            ) : (
              <FontAwesomeIcon icon={faBars} size="lg" className="text-white" />
            )}
          </button>

          {/* Logo */}
          <div className="flex-shrink-0">
            <NavLink to="/">
              <img
                className="h-10 md:h-10 xl:h-14 lg:h-14"
                alt="Trackit"
                src="/trackitt-logo.png"
              />
            </NavLink>
          </div>

          {/* Profile Photo */}
          <div className="lg:hidden">
            {user && (
              <div
                className="relative inline-block text-left"
                ref={dropdownRef}
              >
                {/* Profile Picture */}
                <img
                  src={profilePicture?.fileUrl || "/default-profile-img.png"}
                  alt="Profile"
                  onClick={toggleDropdown}
                  className="w-8 h-8 rounded-lg cursor-pointer border border-gray-500"
                />

                {/* Dropdown */}
                {isOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <ul className="py-2 text-sm text-gray-700">
                      <AutoLogin navButton variant="mobile-dropdown" />
                      <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
                        {!user?.role ? (
                          <></>
                        ) : (
                          <NavLink
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              signOut();
                            }}
                            className="text-gray-700 text-lg"
                          >
                            Sign Out
                          </NavLink>
                        )}
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-4">
            <div className="ml-10 flex items-center space-x-8">
              {user && user.role && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={dashboardPath}
                >
                  Dashboard
                </NavLink>
              )}
              <NavLink
                className={({ isActive, isPending }) =>
                  isPending
                    ? style.isPending
                    : isActive
                      ? style.active
                      : style.inactive
                }
                to={"/"}
              >
                Home
              </NavLink>
              <NavLink
                className={({ isActive, isPending }) =>
                  isPending
                    ? style.isPending
                    : isActive
                      ? style.active
                      : style.inactive
                }
                to={"/events"}
              >
                Events
              </NavLink>
              {!user?.role && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={"/Contact"}
                >
                  Contact Us
                </NavLink>
              )}

              {canViewLeaderboard && user?.role !== "highschool" ? (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={"/leaderboard"}
                >
                  Leaderboard
                </NavLink>
              ) : null}
              {!user?.role ? (
                <>
                  <NavLink
                    className={({ isActive, isPending }) =>
                      isPending
                        ? style.isPending
                        : isActive
                          ? style.active
                          : style.inactive
                    }
                    to={"/register/coach"}
                  >
                    University Registration
                  </NavLink>
                  <NavLink
                    className="text-xl bg-red-500 text-white px-8 py-2 rounded-full uppercase "
                    to={"/sign-in"}
                  >
                    Sign In
                  </NavLink>
                </>
              ) : (
                <>
                  <AutoLogin navButton />
                  <NavLink
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      signOut();
                    }}
                    className={({ isActive, isPending }) =>
                      isPending
                        ? style.isPending
                        : isActive
                          ? style.active
                          : style.inactive
                    }
                  >
                    Sign Out
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        </div>

        {/* Background Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 top-16"
            onClick={toggleMobileMenu}
          ></div>
        )}

        {/* Mobile Menu */}
        <div
          className={`fixed top-16 inset-y-0 left-0 bg-[#545353] w-72 transform transition-transform duration-300 ease-in-out z-50 md:block ${
            isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <button
            className="absolute top-4 right-4 text-gray-700 hover:text-gray-900"
            onClick={toggleMobileMenu}
          ></button>
          <nav className="flex flex-col space-y-4 py-6 px-4">
            <div className="p-6 sm:px-3">
              {user && user.role && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                  }
                  to={dashboardPath}
                >
                  Dashboard
                </NavLink>
              )}
              <NavLink
                className={({ isActive, isPending }) =>
                  `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                }
                to={"/"}
              >
                Home
              </NavLink>
              <NavLink
                className={({ isActive, isPending }) =>
                  `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                }
                to={"/events"}
              >
                Events
              </NavLink>

              {canViewLeaderboard && user?.role !== "highschool" && (
                <NavLink
                  className={({ isActive, isPending }) =>
                    isPending
                      ? style.isPending
                      : isActive
                        ? style.active
                        : style.inactive
                  }
                  to={"/leaderboard"}
                >
                  Leaderboard
                </NavLink>
              )}

              {!user?.role ? (
                <>
                  <NavLink
                    className={({ isActive, isPending }) =>
                      `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                    }
                    to={"/register/coach"}
                  >
                    University Registration
                  </NavLink>
                  <NavLink
                    className={({ isActive, isPending }) =>
                      `${isPending ? style.isPending : isActive ? style.active : style.inactive} pb-8 block`
                    }
                    to={"/sign-in"}
                  >
                    Sign In
                  </NavLink>
                </>
              ) : (
                <>
                  <AutoLogin navButton variant="mobile-menu" />
                  <NavLink
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      signOut();
                    }}
                    className={({ isActive, isPending }) =>
                      `${isPending ? style.isPending : isActive ? style.active : style.inactive} pt-8 block`
                    }
                  >
                    Sign Out
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        </div>
      </nav>
    </>
  );
};
export default Navbar;
